import { Col, Row } from "antd";
import { useState, useMemo, useCallback } from "react";
import { useRouteMatch } from "react-router";
import { Box, Text, Flex, Input, SimpleGrid } from "@chakra-ui/react";

import { useBreadcrumb } from "features/breadcrumbs";
import useDetalhesCobranca from "core/features/cobranca/hooks/useDetalhesCobranca";

import HistoricoDeAcordo from "./components/HistoricoDeAcordo";
import HistoricoDeCobranca from "./components/HistoricoDeCobranca";
import DadosDeContato from "./components/DadosDeContato";
import Tabs from "components/Tabs";
import { useCurrentTenant } from "features/auth/tenant.store";
import HeaderUnidade from "components/HeaderUnidade";
import PrimaryButton from "components/Button/PrimaryButton";
import history from "infra/helpers/history";
import { showError, showSuccess, showErrorAxios } from "infra/helpers/alerta";
import Container from "components/Container";
import ItemCombranca from "./components/ItemCobranca";
import { useLoading } from "ui/components/GlobalLoading";
import OutlineButton from "ui/components/Button/OutlineButton";
import moment from "moment";
import ModalNovoAcordo from "components/Features/Acordo/NovoAcordo";
import colors from "core/resources/theme/colors";
import ExportMenu from "components/ExportMenu";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import { useTenantInfo } from "core/features/relatorios/hooks/useTenantInfo";
import PDFRelatorioCobrancas from "./components/PDFRelatorio";
import { exportExcelRelatorioCobrancas } from "./components/util/helper";
import { formatBlocoQuadra } from "infra/helpers/formatBlocoQuadra";
import { Checkbox } from "antd";
import useCobrancaContasComposicoes from "features/cobranca/hooks/useCobrancaContasComposicoes";
import useSaveAcordo from "features/acordo/hooks/useSaveAcordo";
import SelectableButton from "components/Button/SelectableButton";
import { CobrancaContaDetailsItem } from "core/features/cobranca/typings";
import { roundTo } from "round-to";
import BottomTableFixed from "components/BottomTableFixed";
import { formatMoney } from "infra/helpers/number";

function DetalhesPage() {
  const route = useRouteMatch<{ id: string }>();
  const [data, setData] = useState<moment.Moment | null>(moment());
  const [incluirContasPendentes, setIncluirContasPendentes] = useState<
    "0" | "1"
  >("0");

  const [isOpenModalAcordo, setIsOpenModalAcordo] = useState(false);
  const [taxasModificadas, setTaxasModificadas] = useState<
    Record<
      string,
      Record<
        string,
        { taxaMulta: number; taxaJuros: number; taxaCobranca: number }
      >
    >
  >({});
  const [contasModificadas, setContasModificadas] = useState<
    Record<string, CobrancaContaDetailsItem>
  >({});

  const {
    data: detalhesCobranca,
    isLoading: isLoadingDetalhes,
    isFetching: isFetchingDetalhes,
    refetch,
  } = useDetalhesCobranca(
    route.params.id,
    data?.format("YYYY-MM-DD") || moment().format("YYYY-MM-DD"),
    incluirContasPendentes
  );

  const { mutate: handleSave, isLoading: isLoadingSave } = useSaveAcordo();

  const { tenant } = useCurrentTenant();
  const [selectedContasIds, setSelectedContasIds] = useState<string[]>([]);

  const nomeUnidade = formatBlocoQuadra(
    detalhesCobranca?.numero,
    detalhesCobranca?.blocoQuadra
  );

  useBreadcrumb([
    { name: "Financeiro" },
    { name: "Receita" },
    { name: "Cobranças", url: "/financeiro/receita/cobranca" },
    {
      name: nomeUnidade,
    },
  ]);

  const _isLoading = isLoadingDetalhes || isFetchingDetalhes;

  useLoading(_isLoading);

  const {
    isLoading: isLoadingInfo,
    isFetching: isFetchingInfo,
    refetch: refetchInfo,
  } = useTenantInfo(false);

  const { onDownload: _onExportPdf } = useDownloadRenderedPdf({
    fileName: `Cobranças unidade ${nomeUnidade} - ${moment(data).format(
      "DD_MM_YYYY"
    )}`,
    Component: PDFRelatorioCobrancas,
    loading: isLoadingInfo || isFetchingInfo,
  });

  const getDetalhesCobrancaComContasModificadas = () => {
    if (detalhesCobranca && Object.values(contasModificadas).length) {
      const _detalhesCobranca = { ...detalhesCobranca };

      _detalhesCobranca.contas = detalhesCobranca.contas.map((conta) => {
        return contasModificadas[conta.id] ?? conta;
      });

      return _detalhesCobranca;
    }

    return detalhesCobranca;
  };

  const _detalhesCobranca = getDetalhesCobrancaComContasModificadas();

  const contasSelecionadas = _detalhesCobranca?.contas?.filter((c) =>
    selectedContasIds.includes(c.id)
  );

  const totalSelecionado = roundTo(
    contasSelecionadas?.reduce(
      (acc, next) => (acc += roundTo(next.total, 2)),
      0
    ) ?? 0,
    2
  );

  const onExportPdf =
    !_isLoading && _detalhesCobranca?.contas?.length
      ? () => {
          refetchInfo().then(({ data: tenantInfo }) => {
            if (tenantInfo) {
              _onExportPdf({
                data: {
                  ...tenantInfo,
                  ..._detalhesCobranca,
                  dataCalculo: data?.format("DD/MM/YYYY"),
                  nomeUnidade,
                },
              });
            }
          });
        }
      : undefined;

  const onExportExcel =
    !_isLoading && _detalhesCobranca?.contas.length
      ? () => {
          refetchInfo().then(({ data: tenantInfo }) => {
            if (tenantInfo) {
              exportExcelRelatorioCobrancas({
                data: {
                  ...tenantInfo,
                  ..._detalhesCobranca,
                  dataCalculo: data?.format("DD/MM/YYYY"),
                  nomeUnidade,
                },
              });
            }
          });
        }
      : undefined;

  const allContasIds = useMemo(
    () => detalhesCobranca?.contas.map((conta) => conta.id) ?? [],
    [detalhesCobranca]
  );

  const isInderteminate =
    selectedContasIds.length > 0 &&
    selectedContasIds.length < allContasIds.length;

  const isChecked = selectedContasIds.length === allContasIds.length;

  const selectAll = () => {
    if (isChecked) {
      setSelectedContasIds([]);
    } else setSelectedContasIds(allContasIds);
  };

  const onChangeTaxas = ({
    contaId,
    categoriaFinanceiraId,
    taxas,
  }: {
    contaId: string;
    categoriaFinanceiraId: string;
    taxas: { taxaMulta: number; taxaJuros: number; taxaCobranca: number };
  }) => {
    if (taxasModificadas[contaId]) {
      setTaxasModificadas((state) => ({
        ...state,
        [contaId]: {
          ...taxasModificadas[contaId],
          [categoriaFinanceiraId]: taxas,
        },
      }));
    } else {
      setTaxasModificadas((state) => ({
        ...state,
        [contaId]: { [categoriaFinanceiraId]: taxas },
      }));
    }
  };

  const onContaModificada = useCallback((conta: CobrancaContaDetailsItem) => {
    setContasModificadas((state) => ({ ...state, [conta.id]: conta }));
  }, []);

  return (
    <Container pb="32px">
      {isOpenModalAcordo ? (
        <ModalNovoAcordo
          unidadeId={route.params?.id}
          isOpen={isOpenModalAcordo}
          onClose={() => {
            setIsOpenModalAcordo(false);
          }}
          fetchComposicoesHook={useCobrancaContasComposicoes}
          fetchComposicoesHookParams={{
            contas: selectedContasIds,
            unidadeId: route.params.id,
            dataAtualizacao:
              data?.format("YYYY-MM-DD") || moment().format("YYYY-MM-DD"),
          }}
          fetchComposicoesHookBody={{ taxasModificadas }}
          isLoadingSave={isLoadingSave}
          onSubmit={(data) => {
            handleSave(
              {
                ...data,
                contas: selectedContasIds,
              },
              {
                onSuccess: async () => {
                  await showSuccess();

                  setIsOpenModalAcordo(false);
                  history.push(`/financeiro/receita/cobranca`);
                },
                onError: (err: any) => {
                  showErrorAxios(err);
                },
              }
            );
          }}
        />
      ) : null}

      <Row gutter={32}>
        <Col sm={24}>
          <Box>
            <HeaderUnidade
              bloco={detalhesCobranca?.blocoQuadra}
              nome={detalhesCobranca?.proprietario?.nome!}
              unidade={detalhesCobranca?.numero!}
            />
          </Box>

          <Box mt="32px">
            <Flex mb={4}>
              <Flex flex={1}>
                <Text mt="6px" fontSize="lg" mr={4}>
                  Contas em atraso/pendente
                </Text>
                <ExportMenu
                  onExportPdf={onExportPdf}
                  onExportarXlsx={onExportExcel}
                  variant="transparent"
                  text="Mais ações"
                />
                <Flex mt="2px">
                  <SelectableButton
                    selectedColor={colors.green500}
                    rounded={6}
                    selected={incluirContasPendentes === "0"}
                    onClick={() => setIncluirContasPendentes("0")}
                    ml={4}
                  >
                    Apenas vencidas
                  </SelectableButton>
                  <SelectableButton
                    selectedColor={colors.green500}
                    rounded={6}
                    selected={incluirContasPendentes === "1"}
                    onClick={() => setIncluirContasPendentes("1")}
                    ml={4}
                  >
                    Vencidas e a vencer
                  </SelectableButton>
                </Flex>
              </Flex>

              <Flex gap={3}>
                <Flex gap={3}>
                  <Input
                    type="date"
                    value={data?.format("YYYY-MM-DD")}
                    onChange={(ev) => setData(moment(ev.target.value))}
                    bg="white !important"
                    borderWidth={1}
                    borderColor={`${colors.gray} !important`}
                    variant="outline"
                  />

                  <OutlineButton
                    px={8}
                    onClick={() => refetch()}
                    isLoading={_isLoading}
                  >
                    Recalcular
                  </OutlineButton>
                </Flex>

                <PrimaryButton
                  isLoading={_isLoading}
                  onClick={() => {
                    if (!selectedContasIds.length) {
                      return showError(
                        "Selecione pelo menos uma conta para gerar o acordo!"
                      );
                    }

                    setIsOpenModalAcordo(true);
                  }}
                >
                  Fazer acordo
                </PrimaryButton>
              </Flex>
            </Flex>
            {/* <TabelaCobranca
              detalhesCobranca={detalhesCobranca?.contas ?? []}
              isLoading={isLoading}
              onSelected={setSelecteds}
            /> */}

            {detalhesCobranca?.contas?.length ? (
              <Flex align="center" ml={6} mb={4}>
                <Checkbox
                  onChange={() => selectAll()}
                  checked={isChecked}
                  indeterminate={isInderteminate}
                >
                  Selecionar todos
                </Checkbox>
              </Flex>
            ) : null}

            {detalhesCobranca?.contas.map((conta, index) => (
              <ItemCombranca
                unidadeId={route.params.id}
                rowData={{
                  ...conta,
                  primeiroItem: index === 0,
                  ultimoItem: index === detalhesCobranca.contas.length - 1,
                }}
                dataPosicao={
                  data?.format("YYYY-MM-DD") || moment().format("YYYY-MM-DD")
                }
                conta={conta}
                key={conta.id}
                selected={selectedContasIds}
                setSelected={setSelectedContasIds}
                onChangeTaxas={onChangeTaxas}
                taxasModificadas={taxasModificadas[conta.id]}
                onContaModificada={onContaModificada}
              />
            ))}
          </Box>

          <Box mt="32px">
            <Tabs.Tab>
              <Tabs.Panel title={"Contato"}>
                <SimpleGrid columns={2} gridGap={4}>
                  <DadosDeContato dataCobranca={detalhesCobranca!} />

                  <HistoricoDeCobranca
                    pessoaId={detalhesCobranca?.proprietario?.id!}
                    tenantId={tenant.id}
                    unidadeId={detalhesCobranca?.id!}
                  />
                </SimpleGrid>
              </Tabs.Panel>

              <Tabs.Panel title={"Histórico de acordos"}>
                <HistoricoDeAcordo unidadeId={detalhesCobranca?.id} />
              </Tabs.Panel>
            </Tabs.Tab>
          </Box>
        </Col>
      </Row>

      {selectedContasIds?.length ? (
        <BottomTableFixed
          description={`Total: ${formatMoney(totalSelecionado)}`}
          selectedRows={selectedContasIds}
          onClose={() => setSelectedContasIds([])}
        />
      ) : null}
    </Container>
  );
}

export default DetalhesPage;
